import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import Button from '../common/Button';
import { Modal, ModalContent, ModalCloseButton } from '../common';
import { Heading, Text, Typography } from '../common/Typography';
import { supabase } from '../../utils/supabase';
import { getGenderTextColor, getGenderAvatarBg } from '../../utils/genderColors';

const CompletedSessionModal = ({ 
  sessionId, 
  isOpen, 
  onClose
}) => {
  const [session, setSession] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && sessionId) {
      fetchSessionData();
    }
  }, [isOpen, sessionId]);

  const fetchSessionData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { data: sessionData, error: sessionError } = await supabase
        .from('sessions')
        .select(`*, session_games ( id )`)
        .eq('id', sessionId)
        .single();

      if (sessionError) throw sessionError;

      const { data: participantsData, error: participantsError } = await supabase
        .from('session_players')
        .select(`
          player_id,
          players:player_id ( id, first_name, last_name, gender )
        `)
        .eq('session_id', sessionId)
        .not('players', 'is', null)
        .order('players(first_name)', { ascending: true });

      if (participantsError) throw participantsError;

      setSession(sessionData);
      setParticipants(participantsData || []);
    } catch (err) {
      console.error('Error fetching session data:', err);
      setError('Failed to load session details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => format(new Date(dateString), 'EEEE, MMMM dd, yyyy');
  const formatTime = (dateString) => format(new Date(dateString), 'h:mm a');

  const getSessionTypeBadge = (sessionType) => {
    const badges = {
      competitive: { label: 'Competitive', color: 'bg-info-light text-info-dark' },
      social:      { label: 'Social',       color: 'bg-success-light text-success-dark' },
      private:     { label: 'Private',      color: 'bg-surface-subtle text-text-secondary border border-border-medium' },
    };
    return badges[sessionType] || badges.competitive;
  };

  const badge = session ? getSessionTypeBadge(session.session_type) : null;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContent size="medium" padding={false} className="flex min-h-0 flex-col overflow-hidden">
        <div className="sticky top-0 z-10 shrink-0 border-b border-border-medium bg-surface px-4 py-3 sm:px-6 sm:py-4 flex items-center justify-between">
          <Heading level={2} className="text-text-primary">
            Session Details
          </Heading>
          <ModalCloseButton onClose={onClose} />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <Text className="mt-2 text-text-secondary">Loading session details...</Text>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <Text className="text-danger">{error}</Text>
            </div>
          ) : session ? (
            <>
              {/* Session Info */}
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  <Heading level={3} className="text-text-primary">{session.name || 'Untitled Session'}</Heading>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${badge.color}`}>
                    {badge.label}
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-surface-subtle text-text-secondary border border-border-medium">
                    Completed
                  </span>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center text-text-secondary">
                    <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <Text>{formatDate(session.session_date)}</Text>
                  </div>
                  <div className="flex items-center text-text-secondary">
                    <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <Text>{formatTime(session.session_date)}</Text>
                  </div>
                  {session.description && (
                    <div className="mt-3 p-3 bg-surface-subtle rounded-lg border border-border-medium">
                      <Text className="text-text-primary">{session.description}</Text>
                    </div>
                  )}
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-info-light rounded-xl p-4 border-2 border-border-medium">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-text-primary text-xs uppercase font-semibold tracking-wide mb-1">
                        Players
                      </p>
                      <p className="text-2xl font-bold text-info">
                        {participants.length}
                      </p>
                    </div>
                    <svg className="w-8 h-8 text-info flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                </div>
                <div className="bg-success-light rounded-xl p-4 border-2 border-border-medium">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-text-primary text-xs uppercase font-semibold tracking-wide mb-1">
                        Games Played
                      </p>
                      <p className="text-2xl font-bold text-success">
                        {session.session_games?.length || 0}
                      </p>
                    </div>
                    <svg className="w-8 h-8 text-success flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Participants List */}
              <div>
                <Heading level={4} className="text-text-primary mb-3">
                  Participants ({participants.length})
                </Heading>
                {participants.length > 0 ? (
                  <div className="max-h-60 overflow-y-auto bg-surface-subtle rounded-xl p-4 border-2 border-border-medium">
                    <div className="grid grid-cols-2 gap-2">
                      {participants.map((participant) => (
                        <div
                          key={participant.player_id}
                          className="flex items-center p-2 bg-surface rounded-lg border border-border-medium"
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium mr-2 ${getGenderAvatarBg(participant.players?.gender)}`}>
                            {participant.players?.first_name?.[0] || '?'}
                          </div>
                          <Typography variant="body" className={`font-medium ${getGenderTextColor(participant.players?.gender)}`}>
                            {participant.players?.first_name} {participant.players?.last_name}
                          </Typography>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6 bg-surface-subtle rounded-xl border-2 border-border-medium">
                    <Text className="text-text-muted">No participant data available</Text>
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>

        <div className="shrink-0 border-t border-border-medium bg-surface px-4 py-3 sm:px-6 sm:py-4 flex justify-end">
          <Button onClick={onClose} variant="outline">
            Close
          </Button>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default CompletedSessionModal;
